// =============================================================================
// ui/state/range.tsx — the global date range every tab reads.
// -----------------------------------------------------------------------------
// The scope the user actually SETS. Distinct from the loader's 45-day boot
// window, which is plumbing (D57): this is the answer to "what am I looking at",
// and it appears in the toolbar rather than in a caveat.
//
// ── ⚠ THE ANCHOR IS MODE-DEPENDENT, AND THAT IS THE WHOLE HAZARD ──
//
// `rangeBounds` resolves `today`, `yesterday` and `week` relative to an anchor.
// In session mode that anchor is the current SESSION date — ET-anchored, rolling
// forward at 18:00 ET — not the calendar date. Pass the calendar date with
// `mode: 'session'` and every relative preset is off by a day for anyone trading
// the evening, which is the same defect the screen comparison caught twice
// already (Trades displaying `t.date`, Calendar bucketing on it).
//
// So `bounds` is NULL until the mode is known. A tab renders "loading" rather
// than rendering the calendar-mode answer and correcting itself a frame later —
// a range that silently changes after paint is indistinguishable from a bug, and
// worse, is usually not noticed at all.
//
// ── BOTH ANCHORS ARE FROZEN ONCE PER SESSION ──
//
// Same reason `JournalProvider` freezes `today`: recomputing at render means a
// tab left open across midnight (or across 18:00 ET) silently re-buckets every
// screen under a user who has not touched anything.
//
// ── PERSISTENCE ──
//
// localStorage, matching the old client, which keeps this in local state and
// deliberately does NOT sync it to `user_preferences`. A date filter is about
// the sitting, not about the account: syncing it would mean opening the app on
// a phone and finding last Tuesday's custom range applied.
// =============================================================================

import {
  createContext, useCallback, useContext, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_RANGE, currentSessionDate, dateToISO, isRangePreset, rangeBounds, rangeLabel, rangeLabelHuman,
  type DayBoundaryMode, type RangeBounds, type RangeSelection,
} from '../../selectors/range';
import { usePreferences } from './preferences';

const STORAGE_KEY = 'tc.range';

export interface RangeState {
  selection: RangeSelection;
  /** ⚠ NULL until the day-boundary mode is known. See the header. */
  bounds: RangeBounds | null;
  /** The anchor actually used, exposed so a tab can label what it means. */
  today: string | null;
  /**
   * The raw instant both anchors were frozen from.
   *
   * ⚠ For surfaces that need the WALL CLOCK rather than the session day — Goals
   * is the only one today. Exposed so there is ONE clock per mount: a test that
   * pins `now` on this provider pins every date-anchored card beneath it.
   * Reaching for `new Date()` in a component is what made a Goals test pass all
   * week and fail on the Sunday its window rolled over.
   */
  wallClockNow: Date;
  mode: DayBoundaryMode | null;
  label: string;
  /**
   * The same range with its dates spelled out — "This week (May 11 – May 17,
   * 2026)".
   *
   * ⚠ FOR ANYTHING THAT LEAVES THE APP. On screen the short label is right: the
   * picker is two inches away and says which range is selected. In a PNG posted
   * to Discord and read next month, "This week" is not a fact. #43.
   */
  captureLabel: string;
  setRange: (next: RangeSelection) => void;
}

const Ctx = createContext<RangeState | null>(null);

export function useRange(): RangeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRange() outside <RangeProvider>');
  return v;
}

function readStored(): RangeSelection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<RangeSelection>;
    // ⚠ VALIDATED against the real preset list, not merely typeof-checked.
    // Storage is user-editable and survives across versions, so a stored object
    // may carry a preset this build no longer has — and `rangeBounds` treats an
    // unrecognised preset as `all`. The failure would therefore be a SILENTLY
    // WIDENED range: the whole book under a heading claiming this week.
    //
    // The first version of this function checked `typeof p.preset === 'string'`
    // and had this very comment above it, arguing for a check it did not make.
    if (!p || !isRangePreset(p.preset)) return null;
    // Rebuilt field by field rather than trusted whole.
    const out: RangeSelection = { preset: p.preset };
    if (typeof p.customStart === 'string') out.customStart = p.customStart;
    if (typeof p.customEnd === 'string') out.customEnd = p.customEnd;
    return out;
  } catch {
    // A quota error, private mode, or a half-written value. Falling back to the
    // default is right here — unlike a failed DATA read, a missing UI preference
    // has a correct default and no way to be wrong about the numbers.
    return null;
  }
}

export interface RangeProviderProps {
  children: ReactNode;
  /** Injected by tests. Frozen once — see the header. */
  now?: Date;
  /** Injected by tests to bypass storage entirely. */
  initial?: RangeSelection;
}

export function RangeProvider({ children, now, initial }: RangeProviderProps) {
  const { prefs } = usePreferences();
  const mode: DayBoundaryMode | null = prefs?.dayBoundaryMode ?? null;

  // Both anchors, resolved ONCE. Which one is used depends on the mode, and the
  // mode can change mid-session (Settings) — so both are computed up front
  // rather than one being chosen at freeze time.
  // ⚠ THE RAW INSTANT IS KEPT, not just the two ISO days derived from it.
  // Goals windows are anchored to the WALL CLOCK rather than the session day
  // (domain/goals.ts note 3), so they cannot use `today` — and without a shared
  // instant `useGoals` fell back to its own `new Date()`. That is two clocks on
  // one screen: a test could pin the range and not the goals, and the Goals card
  // silently re-anchored at midnight while the rest of the Dashboard did not.
  const [anchors] = useState(() => {
    const t = now ?? new Date();
    return { at: t, calendar: dateToISO(t), session: currentSessionDate(t) as string };
  });

  const [selection, setSelection] = useState<RangeSelection>(
    () => initial ?? readStored() ?? DEFAULT_RANGE,
  );

  const setRange = useCallback((next: RangeSelection) => {
    setSelection(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Persisting is a nicety; the range still applies for this session.
    }
  }, []);

  const today = mode ? (mode === 'session' ? anchors.session : anchors.calendar) : null;

  const bounds = useMemo(
    () => (mode && today ? rangeBounds(selection, today, mode) : null),
    [selection, today, mode],
  );

  const value = useMemo<RangeState>(
    () => ({
      selection, bounds, today, mode,
      label: rangeLabel(selection),
      // ⚠ Falls back to the SHORT label until the day-boundary preference has
      // loaded, because the dates cannot be resolved without it. That window is
      // the same one `bounds` is null in, and no capture can run in it — the
      // Dashboard does not render its region until its data is there.
      captureLabel: mode && today ? rangeLabelHuman(selection, today, mode) : rangeLabel(selection),
      setRange,
      // ⚠ From the frozen anchor, never a fresh `new Date()` — a new instant on
      // every render would re-key `useGoals`'s memo and re-fire its read
      // continuously.
      wallClockNow: anchors.at,
    }),
    [selection, bounds, today, mode, setRange, anchors],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
